<script lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { VariantProps } from 'tailwind-variants';
import type { UploadScope } from '#layers/assets/stores/upload';
import { tv } from 'tailwind-variants';

export const fileUploadSystemThumbnail = tv({
	slots: {
		root: 'group relative cursor-pointer overflow-hidden border',
		image: 'size-full object-cover',
		fallback: 'flex size-full items-center justify-center bg-muted',
		fallbackIcon: 'text-muted',
		overlay: `
			absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity
			group-hover:opacity-100
			group-data-[state=open]:opacity-100
		`,
		overlayIcon: 'text-white',
	},
	variants: {
		size: {
			sm: { root: 'size-8', fallbackIcon: 'size-4', overlayIcon: 'size-3' },
			md: { root: 'size-12', fallbackIcon: 'size-6', overlayIcon: 'size-4' },
			lg: { root: 'size-16', fallbackIcon: 'size-8', overlayIcon: 'size-5' },
		},
		shape: {
			circle: { root: 'rounded-full' },
			square: { root: 'rounded-lg' },
		},
	},
	defaultVariants: {
		size: 'md',
		shape: 'circle',
	},
});

export type FileUploadSystemThumbnailVariants = VariantProps<typeof fileUploadSystemThumbnail>;

export interface FileUploadSystemThumbnailProps {
	/**
	 * Scope for file upload.
	 * @default 'system'
	 */
	scope?: UploadScope;
	/**
	 * Icon to display when no image is selected.
	 * @default 'lucide:user'
	 */
	fallbackIcon?: string;
	/**
	 * Label for the "Change" dropdown item.
	 */
	changeLabel?: string;
	/**
	 * Label for the "Remove" dropdown item.
	 */
	removeLabel?: string;

	size?: FileUploadSystemThumbnailVariants['size'];
	shape?: FileUploadSystemThumbnailVariants['shape'];
}
</script>

<script setup lang="ts">
import type { GlobalFileId } from '#layers/data-model';
import { createReusableTemplate } from '@vueuse/core';
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { useToastResultHandling } from '#imports';
import { useFileUpload } from '#layers/assets/composables/use-file-upload';

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(defineProps<FileUploadSystemThumbnailProps>(), {
	scope: 'system',
	fallbackIcon: 'lucide:user',
});

const model = defineModel<GlobalFileId | null>();

const appConfig = useAppConfig();
const { t } = useI18n();
const { onError } = useToastResultHandling({
	error: { title: t('upload.failed_to_upload_file') },
});
const { uploadFiles, isUploading } = useFileUpload({
	scope: 'system',
	onError,
});

const fileInput = useTemplateRef<HTMLInputElement>('fileInput');

const styles = computed(() => fileUploadSystemThumbnail({
	size: props.size,
	shape: props.shape,
}));

const changeLabel = computed(() => props.changeLabel ?? t('common.change_avatar'));

const menuItems = computed<DropdownMenuItem[][]>(() => [[
	{
		label: changeLabel.value,
		icon: appConfig.ui.icons.edit,
		onSelect: openFilePicker,
	},
	{
		label: props.removeLabel ?? t('common.remove_avatar'),
		icon: appConfig.ui.icons.remove,
		onSelect: () => model.value = null,
	},
]]);

function openFilePicker() {
	if (isUploading.value) return;
	fileInput.value?.click();
}

async function handleFileSelect(event: Event) {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];

	if (!file) return;

	const [uploadedFile] = await uploadFiles([file]);

	if (uploadedFile) {
		model.value = uploadedFile.id;
	}

	input.value = '';
}

const [DefineFallback, ReuseFallback] = createReusableTemplate();
const [DefineButton, ReuseButton] = createReusableTemplate<{ onClick?: () => void }>();
</script>

<template>
	<DefineFallback>
		<span :class="styles.fallback()">
			<slot name="fallback">
				<DIcon :name="fallbackIcon" :class="styles.fallbackIcon()" />
			</slot>
		</span>
	</DefineFallback>

	<DefineButton>
		<button v-bind="$attrs" :class="styles.root()" :disabled="isUploading" type="button" :aria-label="changeLabel">
			<RenderAssetImage v-if="model" :file="model" :project="scope === 'project'" :class="styles.image()">
				<template #error>
					<ReuseFallback />
				</template>
			</RenderAssetImage>
			<ReuseFallback v-else />

			<span v-if="isUploading" class="absolute inset-0 flex items-center justify-center bg-elevated/75">
				<DSpinner />
			</span>
			<span v-else :class="styles.overlay()">
				<DIcon :name="appConfig.ui.icons.edit" :class="styles.overlayIcon()" />
			</span>
		</button>
	</DefineButton>

	<div class="contents">
		<DDropdownMenu v-if="model && !isUploading" :items="menuItems" :content="{ align: 'start' }">
			<ReuseButton />
		</DDropdownMenu>

		<ReuseButton v-else-if="!isUploading" @click="openFilePicker" />

		<ReuseButton v-else />

		<input ref="fileInput" type="file" accept="image/*" hidden :aria-label="$attrs.ariaLabel as string " @change="handleFileSelect">
	</div>
</template>
